import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { apiService } from "../../api/services/apiService";
import type { AgentRunnerBinding, PlatformTenant } from "../../types";
import {
  AGENT_RUNNER_AGENT_IDS,
  AGENT_RUNNER_AGENT_LABELS,
  DEFAULT_AGENT_RUNNER_BASE_PATH,
  type AgentRunnerAgentId,
} from "../../utils/agentRunnerAgents";

const tablePaperSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

const PLATFORM_SCOPE = "__platform__";

function agentLabel(agentId: string | null | undefined): string {
  if (!agentId) {
    return "All agents";
  }
  return AGENT_RUNNER_AGENT_LABELS[agentId as AgentRunnerAgentId] ?? agentId;
}

const AgentRunnersPanel: React.FC = () => {
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [bindings, setBindings] = useState<AgentRunnerBinding[]>([]);
  const [scopeTenantId, setScopeTenantId] = useState(PLATFORM_SCOPE);
  const [loading, setLoading] = useState(true);
  const [savingDefault, setSavingDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [defaultPath, setDefaultPath] = useState(DEFAULT_AGENT_RUNNER_BASE_PATH);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAgentId, setDialogAgentId] = useState<AgentRunnerAgentId>("production-support");
  const [dialogScope, setDialogScope] = useState<"all" | "tenant">("all");
  const [dialogTenantId, setDialogTenantId] = useState("");
  const [dialogBasePath, setDialogBasePath] = useState(DEFAULT_AGENT_RUNNER_BASE_PATH);
  const [dialogSaving, setDialogSaving] = useState(false);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);

  const report = (nextError: string | null, nextSuccess: string | null) => {
    setError(nextError);
    setSuccess(nextSuccess);
  };

  const loadBindings = useCallback((viewTenantId: string) => {
    setLoading(true);
    const tenantId = viewTenantId === PLATFORM_SCOPE ? null : viewTenantId;
    apiService
      .getPlatformAgentRunnerBindings(tenantId)
      .then((rows) => {
        setBindings(rows);
        const platformDefault = rows.find((row) => !row.tenantId && !row.agentId);
        if (platformDefault?.basePath) {
          setDefaultPath(platformDefault.basePath);
        }
      })
      .catch((err: unknown) =>
        report(err instanceof Error ? err.message : "Failed to load agent runner bindings", null),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    apiService
      .getPlatformTenants()
      .then(setTenants)
      .catch((err: unknown) =>
        report(err instanceof Error ? err.message : "Failed to load tenants", null),
      );
  }, []);

  useEffect(() => {
    loadBindings(scopeTenantId);
  }, [loadBindings, scopeTenantId]);

  const platformDefault = useMemo(
    () => bindings.find((row) => !row.tenantId && !row.agentId) ?? null,
    [bindings],
  );

  const perAgentRows = useMemo(
    () => bindings.filter((row) => Boolean(row.agentId)),
    [bindings],
  );

  const saveDefault = () => {
    setSavingDefault(true);
    apiService
      .upsertPlatformAgentRunnerBinding({
        tenantId: null,
        agentId: null,
        basePath: defaultPath,
      })
      .then(() => {
        report(null, "Default runner path saved.");
        loadBindings(scopeTenantId);
      })
      .catch((err: unknown) =>
        report(err instanceof Error ? err.message : "Failed to save default path", null),
      )
      .finally(() => setSavingDefault(false));
  };

  const openAddDialog = () => {
    setDialogAgentId("production-support");
    setDialogScope(scopeTenantId === PLATFORM_SCOPE ? "all" : "tenant");
    setDialogTenantId(scopeTenantId === PLATFORM_SCOPE ? "" : scopeTenantId);
    setDialogBasePath(defaultPath || DEFAULT_AGENT_RUNNER_BASE_PATH);
    setDialogOpen(true);
  };

  const saveBinding = () => {
    if (dialogScope === "tenant" && !dialogTenantId) {
      report("Select a tenant for a tenant-scoped binding.", null);
      return;
    }
    setDialogSaving(true);
    apiService
      .upsertPlatformAgentRunnerBinding({
        tenantId: dialogScope === "tenant" ? dialogTenantId : null,
        agentId: dialogAgentId,
        basePath: dialogBasePath,
      })
      .then(() => {
        setDialogOpen(false);
        report(null, "Agent runner binding saved.");
        loadBindings(scopeTenantId);
      })
      .catch((err: unknown) =>
        report(err instanceof Error ? err.message : "Failed to save binding", null),
      )
      .finally(() => setDialogSaving(false));
  };

  const deleteBinding = (uuid: string) => {
    setDeletingUuid(uuid);
    apiService
      .deletePlatformAgentRunnerBinding(uuid)
      .then(() => {
        report(null, "Binding deleted.");
        loadBindings(scopeTenantId);
      })
      .catch((err: unknown) =>
        report(err instanceof Error ? err.message : "Failed to delete binding", null),
      )
      .finally(() => setDeletingUuid(null));
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ ...tablePaperSx, p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Default runner
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Used for every agent in every tenant unless a more specific row exists. No trailing slash.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            label="Base path"
            value={defaultPath}
            onChange={(event) => setDefaultPath(event.target.value)}
            sx={{ minWidth: 320, flex: 1 }}
            helperText={
              platformDefault
                ? `Last updated by ${platformDefault.updatedBy}`
                : "No row yet — save to create the platform default."
            }
          />
          <Button variant="contained" onClick={saveDefault} disabled={savingDefault}>
            {savingDefault ? "Saving…" : "Save default"}
          </Button>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="binding-scope-label">View scope</InputLabel>
          <Select
            labelId="binding-scope-label"
            label="View scope"
            value={scopeTenantId}
            onChange={(event) => setScopeTenantId(event.target.value)}
          >
            <MenuItem value={PLATFORM_SCOPE}>All tenants (platform-wide)</MenuItem>
            {tenants.map((tenant) => (
              <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                {tenant.displayName ? `${tenant.displayName} (${tenant.tenantId})` : tenant.tenantId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<Add />} onClick={openAddDialog}>
          Add binding
        </Button>
      </Box>

      <TableContainer component={Paper} sx={tablePaperSx}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Agent</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Base path</TableCell>
              <TableCell>Updated by</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && perAgentRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No per-agent bindings. All chats use the default runner path.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              perAgentRows.map((row) => {
                const inherited = scopeTenantId !== PLATFORM_SCOPE && !row.tenantId;
                return (
                  <TableRow key={row.uuid}>
                    <TableCell>{agentLabel(row.agentId)}</TableCell>
                    <TableCell>
                      {row.tenantId ? (
                        <Chip size="small" label={row.tenantId} />
                      ) : (
                        <Chip size="small" variant="outlined" label={inherited ? "Inherited (all tenants)" : "All tenants"} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {row.basePath}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.updatedBy}</TableCell>
                    <TableCell align="right">
                      {!inherited && (
                        <Button
                          color="error"
                          size="small"
                          disabled={deletingUuid === row.uuid}
                          onClick={() => deleteBinding(row.uuid)}
                        >
                          Delete
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add agent runner binding</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="binding-agent-label">Agent</InputLabel>
            <Select
              labelId="binding-agent-label"
              label="Agent"
              value={dialogAgentId}
              onChange={(event) => setDialogAgentId(event.target.value as AgentRunnerAgentId)}
            >
              {AGENT_RUNNER_AGENT_IDS.map((id) => (
                <MenuItem key={id} value={id}>
                  {AGENT_RUNNER_AGENT_LABELS[id]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Scope
            </Typography>
            <RadioGroup
              value={dialogScope}
              onChange={(event) => setDialogScope(event.target.value as "all" | "tenant")}
            >
              <FormControlLabel value="all" control={<Radio />} label="All tenants" />
              <FormControlLabel value="tenant" control={<Radio />} label="One tenant" />
            </RadioGroup>
          </FormControl>
          {dialogScope === "tenant" && (
            <FormControl fullWidth>
              <InputLabel id="binding-tenant-label">Tenant</InputLabel>
              <Select
                labelId="binding-tenant-label"
                label="Tenant"
                value={dialogTenantId}
                onChange={(event) => setDialogTenantId(event.target.value)}
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.displayName ? `${tenant.displayName} (${tenant.tenantId})` : tenant.tenantId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Base path"
            value={dialogBasePath}
            onChange={(event) => setDialogBasePath(event.target.value)}
            helperText="No trailing slash. Example: /api/v2/sami/agent-extra"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveBinding} disabled={dialogSaving}>
            {dialogSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentRunnersPanel;
